import React from 'react';
import { Link } from 'react-router-dom';
import { PageHeader, Section, StatRow } from '../components/PageShell';
import { Button } from '../components/Button';
import { ArrowUpRight } from 'lucide-react';

const cases = [
  {
    id: 'visitor-management',
    client: 'Meridian Facilities',
    industry: 'Facility Management',
    title: 'AI-powered Visitor Management across 40+ sites.',
    challenge: 'Manual paper logs across 40+ facility gates caused 20-minute wait times, poor security posture and zero audit trail.',
    solution: 'Cloud-native VMS with AI face-match, QR passes, host notifications, denylist screening and full audit logging.',
    stack: ['React', 'Next.js', 'Node.js', 'PostgreSQL', 'AWS', 'OpenAI'],
    impact: 'Faster entry, secure premises, complete compliance audit trail.',
    metrics: [
      { label: 'Avg check-in time', value: '−87%' },
      { label: 'Audit compliance', value: '100%' },
      { label: 'Visits processed', value: '3M+' },
    ],
    image: 'https://motionsites.ai/assets/hero-orbit-web3-preview-BXt4OttD.gif',
  },
  {
    id: 'mis',
    client: 'Paradigm Manufacturing',
    industry: 'Manufacturing',
    title: 'Unified MIS with AI-generated executive summaries.',
    challenge: 'Executives had no unified view of ops, finance, HR and CRM — decisions were made on stale spreadsheets.',
    solution: 'End-to-end MIS with a semantic data layer, live dashboards and AI-generated executive summaries.',
    stack: ['Next.js', 'TypeScript', 'FastAPI', 'PostgreSQL', 'Redis', 'Claude'],
    impact: 'Executive-grade visibility across every business unit in real time.',
    metrics: [
      { label: 'Reporting lag', value: '−9 days' },
      { label: 'Forecast accuracy', value: '+38%' },
      { label: 'Sources unified', value: '14 → 1' },
    ],
    image: 'https://motionsites.ai/assets/hero-transform-data-preview-Cx5OU29N.gif',
  },
  {
    id: 'work-permit',
    client: 'Atlas Construction',
    industry: 'Construction',
    title: 'Digital Work Permit Management with AI hazard detection.',
    challenge: 'High-risk work approvals stuck across email, WhatsApp and PDFs — safety incidents were trending up.',
    solution: 'Digital permit-to-work platform with role-based approvals, LOTO, and AI hazard detection from site photos.',
    stack: ['React', 'Node.js', 'Express.js', 'MongoDB', 'Docker', 'Azure'],
    impact: 'Zero-touch approvals, safer sites, insurer-grade audit trails.',
    metrics: [
      { label: 'Approval time', value: '−64%' },
      { label: 'Safety incidents', value: '−72%' },
      { label: 'Standard', value: 'ISO 45001' },
    ],
    image: 'https://motionsites.ai/assets/hero-automation-machines-preview-DlTveRIN.gif',
  },
  {
    id: 'complaint-management',
    client: 'Orbit Retail',
    industry: 'Retail',
    title: 'Omnichannel Complaint Management with AI triage.',
    challenge: 'Tenant and customer complaints scattered across calls, forms and emails with no SLA visibility.',
    solution: 'Omnichannel CMS with AI triage, SLA tracking, escalation paths and satisfaction scoring.',
    stack: ['Next.js', 'Python', 'FastAPI', 'PostgreSQL', 'Gemini', 'LangChain'],
    impact: 'Complaints resolved 3x faster with full accountability.',
    metrics: [
      { label: 'Resolution time', value: '−58%' },
      { label: 'CSAT', value: '+41 pt' },
      { label: 'SLA visibility', value: '100%' },
    ],
    image: 'https://motionsites.ai/assets/hero-aethera-preview-DknSlcTa.gif',
  },
  {
    id: 'facility-erp',
    client: 'Helix Real Estate',
    industry: 'Real Estate',
    title: 'Facility Management ERP with predictive maintenance.',
    challenge: 'Five disconnected tools for assets, PPM, inventory, HR and vendors bloated ops cost by ~30%.',
    solution: 'Unified Facility ERP with predictive maintenance AI, mobile field ops and finance-grade reporting.',
    stack: ['React', 'Node.js', 'PostgreSQL', 'Redis', 'Docker', 'AWS'],
    impact: 'One platform, one data model, one team — measurable OpEx savings.',
    metrics: [
      { label: 'OpEx', value: '−31%' },
      { label: 'Asset uptime', value: '+2.4x' },
      { label: 'Legacy tools retired', value: '4' },
    ],
    image: 'https://motionsites.ai/assets/hero-velorah-preview-CJNTtbpd.gif',
  },
  {
    id: 'ai-automation',
    client: 'Aurora Health',
    industry: 'Healthcare',
    title: 'Enterprise AI Automation Platform with human-in-the-loop.',
    challenge: 'Repetitive back-office work (RCM, HR, finance) drained 12,000+ hours/month across teams.',
    solution: 'AI automation platform with agents, guardrails, human-in-the-loop review and full observability.',
    stack: ['Python', 'FastAPI', 'LangChain', 'OpenAI', 'Claude', 'MongoDB'],
    impact: 'Real, safe automation across finance, HR, procurement and support.',
    metrics: [
      { label: 'Hours saved / mo', value: '+11,400' },
      { label: 'Error rate', value: '−46%' },
      { label: 'Payback', value: '18 wks' },
    ],
    image: 'https://motionsites.ai/assets/hero-nexora-preview-cx5HmUgo.gif',
  },
];

export default function CaseStudies() {
  return (
    <div data-testid="case-studies-page" className="pb-24">
      <PageHeader
        eyebrow="Case Studies"
        title={<>Enterprise <span className="font-serif">work</span> that moved the number.</>}
        subtitle="A selection of engagements where YP Nova was the engineering partner behind the outcome."
      />

      <Section>
        <StatRow
          items={[
            { value: '60+', label: 'Enterprise projects delivered' },
            { value: '11', label: 'Industries served' },
            { value: '$140M+', label: 'Client revenue impacted' },
            { value: '4.9 / 5', label: 'Avg client satisfaction' },
          ]}
        />
      </Section>

      <Section>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {cases.map((c) => (
            <article
              key={c.id}
              data-testid={`case-card-${c.id}`}
              className="rounded-[32px] overflow-hidden bg-white border border-[#7C3AED]/12 shadow-[0_4px_16px_rgba(0,0,0,0.04)] hover:shadow-[0_10px_40px_rgba(109,40,217,0.18)] hover:border-[#7C3AED]/35 transition-shadow flex flex-col"
            >
              <div className="aspect-[16/10] overflow-hidden bg-[#F6FCFF]">
                <img src={c.image} alt={c.client} className="w-full h-full object-cover" />
              </div>
              <div className="p-8 flex flex-col flex-1">
                <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-[#273C46] mb-4">
                  <span>{c.client}</span>
                  <span>·</span>
                  <span>{c.industry}</span>
                </div>
                <h3 className="font-serif text-2xl md:text-3xl text-[#0D212C] leading-tight mb-5">{c.title}</h3>

                <div className="grid grid-cols-1 gap-4 mb-6 text-sm">
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-[#273C46] mb-1">Business Challenge</div>
                    <p className="text-[#0D212C] leading-relaxed">{c.challenge}</p>
                  </div>
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-[#273C46] mb-1">Solution</div>
                    <p className="text-[#0D212C] leading-relaxed">{c.solution}</p>
                  </div>
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-[#273C46] mb-1">Business Impact</div>
                    <p className="text-[#0D212C] leading-relaxed">{c.impact}</p>
                  </div>
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-[#273C46] mb-2">Technology Stack</div>
                    <div className="flex flex-wrap gap-2">
                      {c.stack.map((s) => (
                        <span key={s} className="text-xs px-2.5 py-1 rounded-full bg-[#7C3AED]/8 text-[#4C1D95] font-mono">
                          {s}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4 mt-auto pt-6 border-t border-[#051A24]/10">
                  {c.metrics.map((m) => (
                    <div key={m.label}>
                      <div className="text-lg md:text-xl font-serif text-[#0D212C]">{m.value}</div>
                      <div className="text-[11px] text-[#273C46] leading-snug mt-1">{m.label}</div>
                    </div>
                  ))}
                </div>

                <Link
                  to="/contact"
                  className="mt-6 inline-flex items-center gap-1 text-sm text-[#051A24] hover:opacity-70 underline underline-offset-4"
                >
                  View Case Study <ArrowUpRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            </article>
          ))}
        </div>

        <div className="mt-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <p className="text-sm text-[#273C46]">More detailed case studies available on request under NDA.</p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-1 text-[#051A24] hover:opacity-70 underline underline-offset-4"
          >
            Request the full portfolio <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </Section>

      <Section>
        <div className="rounded-[40px] bg-gradient-to-br from-[#051A24] via-[#1E1B4B] to-[#4C1D95] text-white p-10 md:p-16 shadow-[0_20px_60px_rgba(76,29,149,0.25)] relative overflow-hidden flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <div>
            <h2 className="font-serif text-3xl md:text-5xl mb-3">Have a project like these?</h2>
            <p className="text-[#E0EBF0] max-w-lg">We onboard a limited number of new engagements each quarter. Let&rsquo;s see if there&rsquo;s a fit.</p>
          </div>
          <Button to="/contact" variant="secondary" className="!bg-white !text-[#051A24]">Book a Discovery Call</Button>
        </div>
      </Section>
    </div>
  );
}
